import ConfirmOrRejectModal from 'src/features/base/components/ConfirmOrRejectModal'
import {
  ItemToResolve,
  CountTypeEnum,
  ReceiveBatchToResolve,
  SwapAddOrRemoveReceiveBatchesResponseData,
} from '../../SwapOrReturnTypes'
import { useForm } from 'react-hook-form'
import * as S from './ModalCountProductQuantityReceive.styles'
import { useEffect, useState } from 'react'
import { handleOnKeyDown } from 'src/utils/Form'
import { useDispatch } from 'react-redux'
import { actions } from '../../SwapOrReturnState'
import ModalAlert from 'src/components/ModalAlert'
import { Typography } from '@mui/material'
import ReceiveBatchTable from './components/ReceiveBatchTable'
import TagPrinterManager from 'src/components/TagPrinterManager'
import { ReceivementShelfTag } from 'src/components/Tags/ReceivementShelfTag'
import BatchMovementTag from 'src/components/Tags/BatchMovement/BatchMovementTag'
import dayjs from 'dayjs'

interface ModalCountProductQuantityReceiveProps {
  show: boolean
  product: ItemToResolve
  onConfirm: () => void
  onClose: () => void
}

interface ModalFormProps {
  productBarcode: string
  dueDate: string
  quantityPerBox: number
  boxQuantity: number
  singleQuantity: number
  totalQuantity: number
}

export const ModalCountProductQuantityReceive = ({
  show,
  product,
  onConfirm,
  onClose,
}: ModalCountProductQuantityReceiveProps) => {
  const {
    register, setValue, getValues, setFocus, reset, setError, watch, clearErrors, trigger, resetField, formState: { errors },
  } = useForm<ModalFormProps>()

  const dispatch = useDispatch()

  const [focusOn, setFocusOn] = useState<string>('')

  const [showTagManager, setShowTagManager] = useState<boolean>(false)
  const [tags, setTags] = useState<JSX.Element[]>([])

  const [showAlertModal, setShowAlertModal] = useState<boolean>(false)
  const [alertMessage, setAlertMessage] = useState<JSX.Element>(<></>)

  const [validatedProductBarcode, setValidatedProductBarcode] = useState<boolean>(false)

  const [lastBarcode, setLastBarcode] = useState<string | undefined>(undefined)

  const [preventDoubleClick, setPreventDoubleClick] = useState<boolean>(false)

  const [receivedBatches, setReceivedBatches] = useState<ReceiveBatchToResolve[]>([])

  const quantityPerBox = watch('quantityPerBox', 0)
  const boxQuantity = watch('boxQuantity', 0)
  const singleQuantity = watch('singleQuantity', 0)

  useEffect(() => {
    reset()
    clearErrors()
    setValidatedProductBarcode(false)
    setLastBarcode(undefined)

    setValue('totalQuantity', 0)

    setReceivedBatches(product.receiveBatches || [])
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [show])

  useEffect(() => {
    const qtyPerBox = Number.isNaN(quantityPerBox) || !quantityPerBox ? 0 : Number(quantityPerBox)
    const boxQty = Number.isNaN(boxQuantity) || !boxQuantity ? 0 : Number(boxQuantity)
    const singleQty = Number.isNaN(singleQuantity) || !singleQuantity ? 0 : Number(singleQuantity)

    const total = (qtyPerBox * boxQty) + singleQty

    setValue('totalQuantity', total)
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [quantityPerBox, boxQuantity, singleQuantity])

  const addBatch = async () => {
    if (preventDoubleClick) return

    setPreventDoubleClick(true)

    clearErrors()

    if (!validatedProductBarcode) {
      setError('productBarcode', { message: 'Código de barras não validado!' })
      setPreventDoubleClick(false)
      return
    }

    const validateDueDate = await trigger('dueDate')
    const validateQuantityPerBox = await trigger('quantityPerBox')
    const validateBoxQuantity = await trigger('boxQuantity')
    const validateSingleQuantity = await trigger('singleQuantity')

    if (!validateDueDate || !validateQuantityPerBox || !validateBoxQuantity || !validateSingleQuantity) {
      setPreventDoubleClick(false)
      return
    }

    const dueDate = getValues('dueDate')
    const nQuantityPerBox = Number(quantityPerBox)
    const nBoxQuantity = Number(boxQuantity)
    const nSingleQuantity = Number(singleQuantity)

    const totalQuantity = (nQuantityPerBox * nBoxQuantity) + nSingleQuantity

    setReceivedBatches([
      ...receivedBatches,
      {
        quantity: totalQuantity,
        dueDate,
      },
    ])

    resetField('dueDate')
    resetField('quantityPerBox')
    resetField('boxQuantity')
    resetField('singleQuantity')
    setFocus('dueDate')

    setPreventDoubleClick(false)
  }

  const removeBatch = (index: number) => {
    if (preventDoubleClick) return

    setPreventDoubleClick(true)

    const updatedReceiveBatches = [...receivedBatches]
    if (updatedReceiveBatches[index].idBatch) {
      updatedReceiveBatches[index] = { ...updatedReceiveBatches[index], remove: true }
    } else {
      updatedReceiveBatches.splice(index, 1)
    }

    setReceivedBatches(updatedReceiveBatches)

    setPreventDoubleClick(false)
  }

  const handleCloseTagPrinter = () => {
    setShowTagManager(false)
    setTags([])
  }

  const onSubmit = async () => {
    if (preventDoubleClick) return

    setPreventDoubleClick(true)

    clearErrors()

    if (!validatedProductBarcode) {
      setError('productBarcode', { message: 'Código de barras não validado!' })
      setPreventDoubleClick(false)
      return
    }

    if (receivedBatches.length === 0) {
      setAlertMessage(
        <S.AlertMessageBox>
          <Typography variant='body1'>
            Você deve adicionar ao menos um lote contado!
          </Typography>
        </S.AlertMessageBox>,
      )
      setShowAlertModal(true)
      setPreventDoubleClick(false)
      return
    }

    const totalCountedReceiveQuantity = receivedBatches.reduce((acc, batch) => acc + (batch.remove ? 0 : batch.quantity), 0)

    if (product.returnQuantityCounted) {
      const totalCountedReturnQuantity = product.returnBatches.reduce((acc, batch) => acc + (batch.countedQuantity || 0), 0)
      if (totalCountedReturnQuantity > totalCountedReceiveQuantity) {
        setAlertMessage(
          <S.AlertMessageBox>
            <Typography variant='body1'>
              {`A quantidade total contada (${totalCountedReceiveQuantity}) para receber é menor que a quantidade a retornar (${totalCountedReturnQuantity}).`}
            </Typography>
            <Typography variant='body1'>
              Reconte a quantidade a retornar! A quantidade a receber deve ser maior ou igual a quantidade a retornar!
            </Typography>
          </S.AlertMessageBox>,
        )
        setShowAlertModal(true)
        setPreventDoubleClick(false)
        return
      }
    }

    const onSuccess = (data: SwapAddOrRemoveReceiveBatchesResponseData) => {
      const batchesTags = data.batchesToPrint.map((batchToPrint) => {
        const dataPlusThreeHours = dayjs(batchToPrint.entryDateAndHour).add(3, 'hour')

        const directGondolaTagObj = {
          entryDateAndHour: dataPlusThreeHours.format('DD/MM/YYYY H:mm'),
          batchId: batchToPrint.idBatch,
          modelShopper: data.idModel,
          barcode: data.productBarcode,
          description: data.productDescription,
          fc: data.fulfillmentCenter,
          productCode: data.productCode,
          batchNumber: batchToPrint.batchNumber,
          isDirectShelf: batchToPrint.isDirectGondola,
          addressTag: batchToPrint.address,
        }

        const standardTagObj = {
          lotId: batchToPrint.idBatch,
          batchNumber: batchToPrint.batchNumber,
          modelId: data.idModel,
          productCode: data.productCode,
          productDescription: data.productDescription,
          expirationDate: dayjs(batchToPrint.dueDate).format('DD/MM/YYYY'),
          qty: batchToPrint.quantity,
          measureUnity: ` ${data.measureUnit}`,
          receivementDate: dataPlusThreeHours.format('DD/MM/YYYY'),
          receivementHour: dataPlusThreeHours.format('HH:mm:ss'),
          operatorName: data.operator,
          fc: data.fulfillmentCenter,
          addressTag: batchToPrint.address,
        }

        if (batchToPrint.isDirectGondola) {
          return (
            <ReceivementShelfTag
              key={directGondolaTagObj.batchId}
              obj={{
                ...directGondolaTagObj,
                printed: false,
              }}
            />
          )
        }
        return (
          <BatchMovementTag
            key={standardTagObj.lotId}
            obj={{
              ...standardTagObj,
              printed: false,
            }}
            isProductNotFound={false}
          />
        )
      })
      setTags(batchesTags)
      setShowTagManager(true)

      setPreventDoubleClick(false)
      onConfirm()
    }

    dispatch(actions.swapAddOrRemoveReceiveBatches({
      idItem: product.idItem,
      idModel: product.idModel,
      barcode: getValues('productBarcode'),
      receiveBatches: receivedBatches,
      onSuccess,
      onError: () => setPreventDoubleClick(false),
    }))
  }

  const validateProductBarcode = () => {
    if (preventDoubleClick) return

    setPreventDoubleClick(true)

    clearErrors('productBarcode')

    const productBarcode = getValues('productBarcode')

    if (!productBarcode) {
      setError('productBarcode', { message: 'Campo obrigatório' })
      setPreventDoubleClick(false)
      return
    }

    const onSuccess = () => {
      setValidatedProductBarcode(true)
      setLastBarcode(undefined)
      setTimeout(() => {
        setFocus('dueDate')
      }, 1)
      setPreventDoubleClick(false)
    }

    const onError = (message: string) => {
      setError('productBarcode', { message })
      setLastBarcode(productBarcode)
      setPreventDoubleClick(false)
    }

    dispatch(actions.validateSwapOrReturnToResolve({
      itemId: product.idItem,
      productBarcode,
      registerBarcode: lastBarcode !== undefined && lastBarcode === productBarcode,
      onSuccess,
      onError,
    }))
  }

  const internalOnClose = () => {
    if (preventDoubleClick) return
    onClose()
  }

  const getTomorrowDate = () => {
    const today = new Date()
    today.setDate(today.getDate() + 1)
    return today.toISOString().split('T')[0]
  }

  return (
    <>
      <ConfirmOrRejectModal
        show={show}
        title='Contagem de produto a RECEBER'
        onConfirm={onSubmit}
        onReject={internalOnClose}
        onClose={internalOnClose}
        customizableDescription={(
          <S.Form>
            <S.ProductInfoBox>
              {product.productDescription}
            </S.ProductInfoBox>
            <S.CustomInputText
              inputProps={{
                ...register('productBarcode'),
                'data-testid': 'product-barcode-input-test-id',
              }}
              id='productBarcode'
              name='productBarcode'
              error={!!errors.productBarcode}
              helperText={errors.productBarcode?.message}
              testId='product-barcode-input-test-id'
              label='Bipe o código do produto'
              type='text'
              autoComplete='off'
              disabled={validatedProductBarcode || preventDoubleClick}
              onKeyDown={(e) => handleOnKeyDown(e, '', validateProductBarcode)}
              fullWidth
              autoFocus
              onFocus={() => setFocusOn('productBarcode')}
              onBlur={() => setFocusOn('')}
              InputLabelProps={{ shrink: !!watch('productBarcode') || focusOn === 'productBarcode' }}
            />
            {(validatedProductBarcode && receivedBatches.length > 0) && (
              <>
                <ReceiveBatchTable
                  measureUnit={product.measureUnit}
                  batches={receivedBatches}
                  removeBatch={removeBatch}
                  disableActions={preventDoubleClick}
                />
                <S.TotalQuantityBox data-testid='total-quantity-box-test-id'>
                  <S.TotalQuantityInfo>Lotes: {receivedBatches.filter((batch) => !batch.remove).length}</S.TotalQuantityInfo>
                  <S.TotalQuantityInfo>Quantidade total à receber: {receivedBatches.reduce((acc, batch) => (batch.remove ? 0 : acc + batch.quantity), 0)} {product.measureUnit}</S.TotalQuantityInfo>
                </S.TotalQuantityBox>
              </>
            )}
            <S.CustomInputText
              inputProps={{
                ...register('dueDate', {
                  required: 'Campo obrigatório',
                  min: {
                    value: getTomorrowDate(),
                    message: 'Validade expirada',
                  },
                  max: {
                    value: '9999-12-31',
                    message: 'Data inválida',
                  },
                }),
                'data-testid': 'due-date-input-test-id',
              }}
              InputLabelProps={{ shrink: true }}
              id='dueDate'
              name='dueDate'
              error={!!errors.dueDate}
              helperText={errors.dueDate?.message}
              testId='due-date-input-test-id'
              label='Data de validade'
              type='date'
              autoComplete='off'
              fullWidth
              disabled={!validatedProductBarcode || preventDoubleClick}
              onKeyDown={(e) => handleOnKeyDown(e, 'quantityPerBox', async () => !await trigger('dueDate') && setFocus('dueDate'))}
              onFocus={() => setFocusOn('dueDate')}
              onBlur={() => setFocusOn('')}
            />
            <S.BoxInputValues>
              <S.SuffixWrap>
                <S.CustomInputText
                  inputProps={{
                    ...register('quantityPerBox', {
                      required: 'Campo obrigatório',
                      min: {
                        value: 0,
                        message: 'Deve ser zero ou um valor positivo',
                      },
                      pattern: {
                        value: !product.isKg ? new RegExp('^[0-9]*$') : new RegExp(''),
                        message: 'Deve ser um número inteiro',
                      },
                    }),
                    'data-testid': 'quantity-per-box-input-test-id',
                  }}
                  id='quantityPerBox'
                  name='quantityPerBox'
                  error={!!errors.quantityPerBox}
                  helperText={errors.quantityPerBox?.message}
                  testId='quantity-per-box-input-test-id'
                  label='Caixaria'
                  type='number'
                  autoComplete='off'
                  disabled={!validatedProductBarcode}
                  onKeyDown={(e) => handleOnKeyDown(e, 'boxQuantity', async () => !await trigger('quantityPerBox') && setFocus('quantityPerBox'))}
                  onFocus={() => setFocusOn('quantityPerBox')}
                  onBlur={() => setFocusOn('')}
                  InputLabelProps={{ shrink: !!quantityPerBox || focusOn === 'quantityPerBox' }}
                  fullWidth
                />
                <S.Suffix>{product.measureUnit.toUpperCase()}</S.Suffix>
              </S.SuffixWrap>
              <S.SuffixWrap>
                <S.CustomInputText
                  inputProps={{
                    ...register('boxQuantity', {
                      required: 'Campo obrigatório',
                      min: {
                        value: 0,
                        message: 'Deve ser zero ou um valor positivo',
                      },
                      pattern: {
                        value: new RegExp('^[0-9]*$'),
                        message: 'Deve ser um número inteiro',
                      },
                    }),
                    'data-testid': 'box-quantity-input-test-id',
                  }}
                  id='boxQuantity'
                  name='boxQuantity'
                  error={!!errors.boxQuantity}
                  helperText={errors.boxQuantity?.message}
                  testId='box-quantity-input-test-id'
                  label='Caixas'
                  type='number'
                  autoComplete='off'
                  disabled={!validatedProductBarcode}
                  onKeyDown={(e) => handleOnKeyDown(e, 'singleQuantity', async () => !await trigger('boxQuantity') && setFocus('boxQuantity'))}
                  onFocus={() => setFocusOn('boxQuantity')}
                  onBlur={() => setFocusOn('')}
                  InputLabelProps={{ shrink: !!boxQuantity || focusOn === 'boxQuantity' }}
                  fullWidth
                />
                <S.Suffix>UN</S.Suffix>
              </S.SuffixWrap>
              <S.SuffixWrap>
                <S.CustomInputText
                  inputProps={{
                    ...register('singleQuantity', {
                      required: 'Campo obrigatório',
                      min: {
                        value: 0,
                        message: 'Deve ser zero ou um valor positivo',
                      },
                      pattern: {
                        value: !product.isKg ? new RegExp('^[0-9]*$') : new RegExp(''),
                        message: 'Deve ser um número inteiro',
                      },
                    }),
                    'data-testid': 'single-quantity-input-test-id',
                  }}
                  id='singleQuantity'
                  name='singleQuantity'
                  error={!!errors.singleQuantity}
                  helperText={errors.singleQuantity?.message}
                  testId='single-quantity-input-test-id'
                  label='Avulso'
                  type='number'
                  autoComplete='off'
                  disabled={!validatedProductBarcode}
                  onKeyDown={(e) => handleOnKeyDown(e, 'quantityPerBox', async () => !await trigger('singleQuantity') && setFocus('singleQuantity'))}
                  onFocus={() => setFocusOn('singleQuantity')}
                  onBlur={() => setFocusOn('')}
                  InputLabelProps={{ shrink: !!singleQuantity || focusOn === 'singleQuantity' }}
                  fullWidth
                />
                <S.Suffix>{product.measureUnit.toUpperCase()}</S.Suffix>
              </S.SuffixWrap>
              <S.SuffixWrap>
                <S.CustomInputText
                  InputLabelProps={{ shrink: true }}
                  inputProps={{
                    ...register('totalQuantity'),
                    'data-testid': 'total-quantity-input-test-id',
                  }}
                  id='totalQuantity'
                  name='totalQuantity'
                  testId='total-quantity-input-test-id'
                  label='Total'
                  type='number'
                  autoComplete='off'
                  disabled
                  fullWidth
                />
                <S.Suffix>{product.measureUnit.toUpperCase()}</S.Suffix>
              </S.SuffixWrap>
            </S.BoxInputValues>
            <S.CustomButton
              title='Adicionar Lote'
              bgColor='blue'
              hoverColor='darkBlueHover'
              letterColor='white'
              data-testid='add-batch-button-test-id'
              fullWidth
              onClick={addBatch}
              disabled={!validatedProductBarcode || preventDoubleClick}
            />
          </S.Form>
      )}
        dataTestId='count-product-receive-quantity-modal-test-id'
        confirmButtonText='Confirmar'
        rejectButtonText='Cancelar'
        size='lg'
      />
      <ModalAlert
        show={showAlertModal}
        title={`TROCA - ${CountTypeEnum.RECEIVE.toUpperCase()}`}
        customizableBody={(
          <S.BodyModalAlert data-testid='modal-alert-body-test-id'>
            {alertMessage}
          </S.BodyModalAlert>
        )}
        action={() => setShowAlertModal(false)}
        centered
        testId='modal-alert-test-id'
      />
      {tags.length > 0 && showTagManager && (
        <TagPrinterManager
          setShowTagModal={setShowTagManager}
          showTagModal={showTagManager}
          labelsToPrint={tags}
          setLabelsToPrint={setTags}
          donePrint={handleCloseTagPrinter}
        />
      )}
    </>
  )
}
